/**
 * SdkDocsPage — /developers/sdk-docs (feature FS-228).
 *
 * Developer-docs landing page. Shows:
 *
 *   1. Hero + link to developers.trackerity.com (external docs host).
 *   2. SDK cards grid — one card per platform/version with badges, docs
 *      button, and a markdown quickstart toggle.
 *   3. Registered-SDK create form — operator can publish a new SDK
 *      catalog entry that overrides the global row-set.
 *   4. Endpoint catalog table — searchable list of `/api/v1/*` endpoints
 *      with method filter chips, summary, required scopes, and expand
 *      rows to peek at request/response schemas.
 *   5. Endpoint create form — post a new endpoint catalog row.
 *
 * Brand: emerald -> sky gradient headline, Inter, zinc borders, 6-8 px
 * rounded surfaces, light theme only. No emojis, no competitor names.
 *
 * Self-contained (no react-query) so vitest-node + react-dom/server
 * can drive a smoke without needing a DOM lib.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	countByMethod,
	createEndpoint,
	createSdk,
	deleteEndpoint,
	deleteSdk,
	DEVELOPERS_BASE_URL,
	EMPTY_ENDPOINT_DRAFT,
	EMPTY_SDK_DRAFT,
	endpointDraftSchema,
	filterEndpointsByMethod,
	formatTimestamp,
	HTTP_METHODS,
	listEndpoints,
	listSdks,
	methodBadgeClass,
	removeEndpointFromList,
	removeSdkFromList,
	renderMarkdown,
	replaceEndpointInList,
	replaceSdkInList,
	scopeBadgeClass,
	sdkDraftSchema,
	searchEndpointsLocal,
	searchSdksLocal,
	sortEndpoints,
	sortSdks,
	type EndpointCatalogEntryView,
	type EndpointDraft,
	type HttpMethod,
	type SdkDocEntryView,
	type SdkDraft,
} from "./api";

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const TEXTAREA = `${INPUT} min-h-[120px] font-mono text-xs`;
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"inline-flex items-center justify-center rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HEADLINE =
	"bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent";
const BADGE =
	"inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SdkDocsPageProps {
	initialQuery?: string;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SdkDocsPage(props: SdkDocsPageProps = {}) {
	const [sdks, setSdks] = useState<SdkDocEntryView[]>([]);
	const [endpoints, setEndpoints] = useState<EndpointCatalogEntryView[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [listError, setListError] = useState<string | null>(null);

	const [sdkDraft, setSdkDraft] = useState<SdkDraft>({ ...EMPTY_SDK_DRAFT });
	const [sdkErrors, setSdkErrors] = useState<Record<string, string>>({});
	const [sdkSubmitting, setSdkSubmitting] = useState<boolean>(false);
	const [sdkSubmitError, setSdkSubmitError] = useState<string | null>(null);

	const [endpointDraft, setEndpointDraft] = useState<EndpointDraft>({
		...EMPTY_ENDPOINT_DRAFT,
	});
	const [endpointErrors, setEndpointErrors] = useState<Record<string, string>>(
		{},
	);
	const [endpointSubmitting, setEndpointSubmitting] = useState<boolean>(false);
	const [endpointSubmitError, setEndpointSubmitError] = useState<string | null>(
		null,
	);

	const [endpointSearch, setEndpointSearch] = useState<string>(
		props.initialQuery ?? "",
	);
	const [sdkSearch, setSdkSearch] = useState<string>("");
	const [methodFilter, setMethodFilter] = useState<HttpMethod[]>([]);
	const [expandedSdkId, setExpandedSdkId] = useState<string | null>(null);
	const [expandedEndpointId, setExpandedEndpointId] = useState<string | null>(
		null,
	);
	const [actionId, setActionId] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	// -----------------------------------------------------------------------
	// Data hydration
	// -----------------------------------------------------------------------

	const reload = useCallback(async () => {
		setLoading(true);
		setListError(null);
		try {
			const [sdkRows, endpointRows] = await Promise.all([
				listSdks(),
				listEndpoints(),
			]);
			setSdks(sortSdks(sdkRows));
			setEndpoints(sortEndpoints(endpointRows));
		} catch (err) {
			setListError(err instanceof Error ? err.message : "Failed to load.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void reload();
	}, [reload]);

	// -----------------------------------------------------------------------
	// Derived
	// -----------------------------------------------------------------------

	const filteredSdks = useMemo(
		() => sortSdks(searchSdksLocal(sdks, sdkSearch)),
		[sdks, sdkSearch],
	);

	const methodFiltered = useMemo(
		() => filterEndpointsByMethod(endpoints, methodFilter),
		[endpoints, methodFilter],
	);

	const filteredEndpoints = useMemo(
		() => sortEndpoints(searchEndpointsLocal(methodFiltered, endpointSearch)),
		[methodFiltered, endpointSearch],
	);

	const methodCounts = useMemo(() => countByMethod(endpoints), [endpoints]);

	// -----------------------------------------------------------------------
	// Handlers — SDK
	// -----------------------------------------------------------------------

	async function onSdkSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setSdkSubmitError(null);
		setNotice(null);
		const parsed = sdkDraftSchema.safeParse(sdkDraft);
		if (!parsed.success) {
			setSdkErrors(parsed.errors);
			return;
		}
		setSdkErrors({});
		setSdkSubmitting(true);
		try {
			const created = await createSdk(parsed.data);
			setSdks((list) => sortSdks(replaceSdkInList(list, created)));
			setSdkDraft({ ...EMPTY_SDK_DRAFT });
			setNotice(`Published ${created.platform} ${created.version}.`);
		} catch (err) {
			setSdkSubmitError(
				err instanceof Error ? err.message : "Failed to publish SDK.",
			);
		} finally {
			setSdkSubmitting(false);
		}
	}

	async function onSdkDelete(id: string) {
		setActionId(id);
		setNotice(null);
		try {
			await deleteSdk(id);
			setSdks((list) => removeSdkFromList(list, id));
			setNotice("SDK entry removed.");
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Failed to remove entry.",
			);
		} finally {
			setActionId(null);
		}
	}

	// -----------------------------------------------------------------------
	// Handlers — endpoint
	// -----------------------------------------------------------------------

	async function onEndpointSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setEndpointSubmitError(null);
		setNotice(null);
		const parsed = endpointDraftSchema.safeParse(endpointDraft);
		if (!parsed.success) {
			setEndpointErrors(parsed.errors);
			return;
		}
		setEndpointErrors({});
		setEndpointSubmitting(true);
		try {
			const created = await createEndpoint(parsed.data);
			setEndpoints((list) =>
				sortEndpoints(replaceEndpointInList(list, created)),
			);
			setEndpointDraft({ ...EMPTY_ENDPOINT_DRAFT });
			setNotice(`Catalogued ${created.method} ${created.path}.`);
		} catch (err) {
			setEndpointSubmitError(
				err instanceof Error ? err.message : "Failed to publish endpoint.",
			);
		} finally {
			setEndpointSubmitting(false);
		}
	}

	async function onEndpointDelete(id: string) {
		setActionId(id);
		setNotice(null);
		try {
			await deleteEndpoint(id);
			setEndpoints((list) => removeEndpointFromList(list, id));
			setNotice("Endpoint removed.");
		} catch (err) {
			setListError(
				err instanceof Error ? err.message : "Failed to remove endpoint.",
			);
		} finally {
			setActionId(null);
		}
	}

	function toggleMethod(method: HttpMethod) {
		setMethodFilter((curr) =>
			curr.includes(method)
				? curr.filter((m) => m !== method)
				: [...curr, method],
		);
	}

	function toggleSdkExpand(id: string) {
		setExpandedSdkId((curr) => (curr === id ? null : id));
	}

	function toggleEndpointExpand(id: string) {
		setExpandedEndpointId((curr) => (curr === id ? null : id));
	}

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<main className="mx-auto w-full max-w-6xl px-6 py-12 sm:py-16">
			<header className="flex flex-col gap-3">
				<h1 className="text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">
					<span className={HEADLINE}>SDK developer docs</span>
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Published Trackerity SDKs, their changelogs, and the REST endpoint
					catalog under <code>/api/v1/*</code>. For deep-dive guides, recipes
					and code samples visit{" "}
					<a
						href={DEVELOPERS_BASE_URL}
						target="_blank"
						rel="noreferrer"
						className="font-medium text-emerald-700 underline hover:text-emerald-800"
					>
						developers.trackerity.com
					</a>
					.
				</p>
			</header>

			{notice ? (
				<div className="mt-6 rounded-[6px] border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
					{notice}
				</div>
			) : null}
			{listError ? (
				<div className="mt-6 rounded-[6px] border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
					{listError}
				</div>
			) : null}

			{/* ---------- SDK catalog ---------- */}
			<section className="mt-10">
				<div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
					<div>
						<h2 className="text-xl font-semibold text-zinc-900">
							Published SDKs
						</h2>
						<p className={HINT}>
							Tenant overrides shadow global rows for a given (platform,
							version).
						</p>
					</div>
					<div className="w-full sm:w-80">
						<label className="sr-only" htmlFor="sdk-search">
							Search SDKs
						</label>
						<input
							id="sdk-search"
							type="search"
							value={sdkSearch}
							onChange={(e) => setSdkSearch(e.currentTarget.value)}
							placeholder="Search by name, platform, version..."
							className={INPUT}
						/>
					</div>
				</div>

				{loading && sdks.length === 0 ? (
					<div className={`${CARD} px-6 py-12 text-center text-sm text-zinc-500`}>
						Loading SDK catalog...
					</div>
				) : filteredSdks.length === 0 ? (
					<div className={`${CARD} px-6 py-12 text-center text-sm text-zinc-500`}>
						No SDK entries yet. Publish one below to get started.
					</div>
				) : (
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{filteredSdks.map((sdk) => (
							<article key={sdk.id} className={`${CARD} flex flex-col p-5`}>
								<header className="flex items-start justify-between gap-2">
									<div>
										<h3 className="text-base font-semibold text-zinc-900">
											{sdk.name}
										</h3>
										<p className={HINT}>Trackerity SDK</p>
									</div>
									<span className={`${BADGE} ${scopeBadgeClass(sdk.scope)}`}>
										{sdk.scope === "tenant" ? "Override" : "Global"}
									</span>
								</header>
								<div className="mt-3 flex flex-wrap gap-2">
									<span className={`${BADGE} bg-sky-50 text-sky-700 border-sky-200`}>
										{sdk.platform}
									</span>
									<span className={`${BADGE} bg-zinc-100 text-zinc-700 border-zinc-200`}>
										v{sdk.version}
									</span>
									{sdk.active ? (
										<span className={`${BADGE} bg-emerald-50 text-emerald-700 border-emerald-200`}>
											Active
										</span>
									) : (
										<span className={`${BADGE} bg-amber-50 text-amber-700 border-amber-200`}>
											Disabled
										</span>
									)}
								</div>
								{sdk.supported_languages.length > 0 ? (
									<p className="mt-3 text-xs text-zinc-500">
										Languages: {sdk.supported_languages.join(", ")}
									</p>
								) : null}
								<div className="mt-4 flex flex-wrap gap-2">
									{sdk.docs_url ? (
										<a
											href={sdk.docs_url}
											target="_blank"
											rel="noreferrer"
											className={BTN_SECONDARY}
										>
											View docs
										</a>
									) : null}
									{sdk.changelog_url ? (
										<a
											href={sdk.changelog_url}
											target="_blank"
											rel="noreferrer"
											className={BTN_SECONDARY}
										>
											Changelog
										</a>
									) : null}
									<button
										type="button"
										onClick={() => toggleSdkExpand(sdk.id)}
										className={BTN_SECONDARY}
									>
										{expandedSdkId === sdk.id ? "Hide" : "Quickstart"}
									</button>
									{sdk.scope === "tenant" ? (
										<button
											type="button"
											onClick={() => void onSdkDelete(sdk.id)}
											disabled={actionId === sdk.id}
											className={BTN_DANGER}
										>
											Remove
										</button>
									) : null}
								</div>
								{expandedSdkId === sdk.id ? (
									<div
										className="mt-4 overflow-auto rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-800"
										// biome-ignore lint/security/noDangerouslySetInnerHtml: rendered from local markdown renderer
										dangerouslySetInnerHTML={{
											__html: renderMarkdown(sdk.quickstart_md),
										}}
									/>
								) : null}
								<p className="mt-3 text-[11px] text-zinc-400">
									Updated {formatTimestamp(sdk.updated_at)}
								</p>
							</article>
						))}
					</div>
				)}
			</section>

			{/* ---------- Publish SDK form ---------- */}
			<section className="mt-10">
				<h2 className="text-xl font-semibold text-zinc-900">
					Publish SDK entry
				</h2>
				<p className={HINT}>
					Writes a tenant-scope override. Global rows shipped by Trackerity ops
					stay intact for tenants without an override.
				</p>
				<form onSubmit={onSdkSubmit} className={`${CARD} mt-4 flex flex-col gap-4 p-5`}>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Name</span>
							<input
								type="text"
								className={INPUT}
								value={sdkDraft.name}
								onChange={(e) =>
									setSdkDraft((d) => ({ ...d, name: e.currentTarget.value }))
								}
								placeholder="Trackerity iOS SDK"
							/>
							{sdkErrors.name ? (
								<span className={ERROR_TEXT}>{sdkErrors.name}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Platform</span>
							<input
								type="text"
								className={INPUT}
								value={sdkDraft.platform}
								onChange={(e) =>
									setSdkDraft((d) => ({
										...d,
										platform: e.currentTarget.value,
									}))
								}
								placeholder="ios"
							/>
							{sdkErrors.platform ? (
								<span className={ERROR_TEXT}>{sdkErrors.platform}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Version (SemVer)</span>
							<input
								type="text"
								className={INPUT}
								value={sdkDraft.version}
								onChange={(e) =>
									setSdkDraft((d) => ({ ...d, version: e.currentTarget.value }))
								}
								placeholder="1.2.3"
							/>
							{sdkErrors.version ? (
								<span className={ERROR_TEXT}>{sdkErrors.version}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Supported languages</span>
							<input
								type="text"
								className={INPUT}
								value={sdkDraft.languagesText}
								onChange={(e) =>
									setSdkDraft((d) => ({
										...d,
										languagesText: e.currentTarget.value,
									}))
								}
								placeholder="swift, objective-c"
							/>
							{sdkErrors.languagesText ? (
								<span className={ERROR_TEXT}>{sdkErrors.languagesText}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Changelog URL</span>
							<input
								type="url"
								className={INPUT}
								value={sdkDraft.changelogUrl}
								onChange={(e) =>
									setSdkDraft((d) => ({
										...d,
										changelogUrl: e.currentTarget.value,
									}))
								}
								placeholder="https://developers.trackerity.com/ios/CHANGELOG"
							/>
							{sdkErrors.changelogUrl ? (
								<span className={ERROR_TEXT}>{sdkErrors.changelogUrl}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Docs URL</span>
							<input
								type="url"
								className={INPUT}
								value={sdkDraft.docsUrl}
								onChange={(e) =>
									setSdkDraft((d) => ({ ...d, docsUrl: e.currentTarget.value }))
								}
								placeholder="https://developers.trackerity.com/ios"
							/>
							{sdkErrors.docsUrl ? (
								<span className={ERROR_TEXT}>{sdkErrors.docsUrl}</span>
							) : null}
						</label>
					</div>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Quickstart markdown</span>
						<textarea
							className={TEXTAREA}
							value={sdkDraft.quickstart}
							onChange={(e) =>
								setSdkDraft((d) => ({
									...d,
									quickstart: e.currentTarget.value,
								}))
							}
							placeholder={"# Install\n\n```swift\nimport TrackeritySDK\n```"}
						/>
						{sdkErrors.quickstart ? (
							<span className={ERROR_TEXT}>{sdkErrors.quickstart}</span>
						) : null}
					</label>
					{sdkSubmitError ? (
						<div className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
							{sdkSubmitError}
						</div>
					) : null}
					<div>
						<button
							type="submit"
							disabled={sdkSubmitting}
							className={BTN_PRIMARY}
						>
							{sdkSubmitting ? "Publishing..." : "Publish SDK entry"}
						</button>
					</div>
				</form>
			</section>

			{/* ---------- Endpoint catalog ---------- */}
			<section className="mt-10">
				<div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
					<div>
						<h2 className="text-xl font-semibold text-zinc-900">
							REST endpoint catalog
						</h2>
						<p className={HINT}>
							Searchable list of <code>/api/v1/*</code> endpoints. Filter by
							method to narrow the view.
						</p>
					</div>
					<div className="w-full sm:w-80">
						<label className="sr-only" htmlFor="endpoint-search">
							Search endpoints
						</label>
						<input
							id="endpoint-search"
							type="search"
							value={endpointSearch}
							onChange={(e) => setEndpointSearch(e.currentTarget.value)}
							placeholder="Search by path or summary..."
							className={INPUT}
						/>
					</div>
				</div>

				<div className="mb-4 flex flex-wrap gap-2">
					{HTTP_METHODS.map((m) => {
						const active = methodFilter.includes(m);
						return (
							<button
								key={m}
								type="button"
								onClick={() => toggleMethod(m)}
								className={`${BADGE} ${methodBadgeClass(m)} ${
									active ? "ring-2 ring-emerald-400 ring-offset-1" : ""
								}`}
							>
								{m} ({methodCounts[m]})
							</button>
						);
					})}
					{methodFilter.length > 0 ? (
						<button
							type="button"
							onClick={() => setMethodFilter([])}
							className={BTN_SECONDARY}
						>
							Clear
						</button>
					) : null}
				</div>

				<div className={`${CARD} overflow-hidden`}>
					<table className="w-full text-left text-sm text-zinc-800">
						<thead className="bg-zinc-50 text-xs uppercase tracking-wide text-zinc-600">
							<tr>
								<th className="px-4 py-2 font-medium">Method</th>
								<th className="px-4 py-2 font-medium">Path</th>
								<th className="px-4 py-2 font-medium">Summary</th>
								<th className="px-4 py-2 font-medium">Scopes</th>
								<th className="px-4 py-2 font-medium">Source</th>
								<th className="px-4 py-2 font-medium" />
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{filteredEndpoints.length === 0 ? (
								<tr>
									<td
										colSpan={6}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										{loading && endpoints.length === 0
											? "Loading endpoint catalog..."
											: "No endpoints catalogued yet."}
									</td>
								</tr>
							) : null}
							{filteredEndpoints.map((endpoint) => (
								<ExpandableEndpointRow
									key={endpoint.id}
									endpoint={endpoint}
									expanded={expandedEndpointId === endpoint.id}
									onToggle={() => toggleEndpointExpand(endpoint.id)}
									onDelete={() => void onEndpointDelete(endpoint.id)}
									deleting={actionId === endpoint.id}
								/>
							))}
						</tbody>
					</table>
				</div>
			</section>

			{/* ---------- Endpoint publish form ---------- */}
			<section className="mt-10">
				<h2 className="text-xl font-semibold text-zinc-900">
					Publish endpoint entry
				</h2>
				<p className={HINT}>
					Writes a tenant-scope override keyed on (method, path).
				</p>
				<form
					onSubmit={onEndpointSubmit}
					className={`${CARD} mt-4 flex flex-col gap-4 p-5`}
				>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Method</span>
							<select
								className={INPUT}
								value={endpointDraft.method}
								onChange={(e) =>
									setEndpointDraft((d) => ({
										...d,
										method: e.currentTarget.value,
									}))
								}
							>
								{HTTP_METHODS.map((m) => (
									<option key={m} value={m}>
										{m}
									</option>
								))}
							</select>
							{endpointErrors.method ? (
								<span className={ERROR_TEXT}>{endpointErrors.method}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Path</span>
							<input
								type="text"
								className={INPUT}
								value={endpointDraft.path}
								onChange={(e) =>
									setEndpointDraft((d) => ({
										...d,
										path: e.currentTarget.value,
									}))
								}
								placeholder="/api/v1/clicks"
							/>
							{endpointErrors.path ? (
								<span className={ERROR_TEXT}>{endpointErrors.path}</span>
							) : null}
						</label>
					</div>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Summary</span>
						<input
							type="text"
							className={INPUT}
							value={endpointDraft.summary}
							onChange={(e) =>
								setEndpointDraft((d) => ({
									...d,
									summary: e.currentTarget.value,
								}))
							}
							placeholder="List recent clicks for the current tenant."
						/>
						{endpointErrors.summary ? (
							<span className={ERROR_TEXT}>{endpointErrors.summary}</span>
						) : null}
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>Required scopes</span>
						<input
							type="text"
							className={INPUT}
							value={endpointDraft.scopesText}
							onChange={(e) =>
								setEndpointDraft((d) => ({
									...d,
									scopesText: e.currentTarget.value,
								}))
							}
							placeholder="tracking:read, tracking:admin"
						/>
						{endpointErrors.scopesText ? (
							<span className={ERROR_TEXT}>{endpointErrors.scopesText}</span>
						) : null}
					</label>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Request schema (JSON)</span>
							<textarea
								className={TEXTAREA}
								value={endpointDraft.requestSchema}
								onChange={(e) =>
									setEndpointDraft((d) => ({
										...d,
										requestSchema: e.currentTarget.value,
									}))
								}
								placeholder='{"type":"object"}'
							/>
							{endpointErrors.requestSchema ? (
								<span className={ERROR_TEXT}>{endpointErrors.requestSchema}</span>
							) : null}
						</label>
						<label className="flex flex-col gap-1">
							<span className={LABEL}>Response schema (JSON)</span>
							<textarea
								className={TEXTAREA}
								value={endpointDraft.responseSchema}
								onChange={(e) =>
									setEndpointDraft((d) => ({
										...d,
										responseSchema: e.currentTarget.value,
									}))
								}
								placeholder='{"type":"object","properties":{"clicks":{"type":"array"}}}'
							/>
							{endpointErrors.responseSchema ? (
								<span className={ERROR_TEXT}>{endpointErrors.responseSchema}</span>
							) : null}
						</label>
					</div>
					{endpointSubmitError ? (
						<div className="rounded-[6px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
							{endpointSubmitError}
						</div>
					) : null}
					<div>
						<button
							type="submit"
							disabled={endpointSubmitting}
							className={BTN_PRIMARY}
						>
							{endpointSubmitting ? "Publishing..." : "Publish endpoint entry"}
						</button>
					</div>
				</form>
			</section>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Expandable row component
// ---------------------------------------------------------------------------

interface ExpandableEndpointRowProps {
	endpoint: EndpointCatalogEntryView;
	expanded: boolean;
	onToggle: () => void;
	onDelete: () => void;
	deleting: boolean;
}

function ExpandableEndpointRow(props: ExpandableEndpointRowProps) {
	const { endpoint, expanded, onToggle, onDelete, deleting } = props;
	return (
		<>
			<tr>
				<td className="px-4 py-3 align-top">
					<span
						className={`${BADGE} ${methodBadgeClass(endpoint.method)}`}
					>
						{endpoint.method}
					</span>
				</td>
				<td className="px-4 py-3 align-top font-mono text-xs text-zinc-800">
					{endpoint.path}
				</td>
				<td className="px-4 py-3 align-top text-sm text-zinc-700">
					{endpoint.summary || "-"}
				</td>
				<td className="px-4 py-3 align-top text-xs text-zinc-500">
					{endpoint.scopes.length > 0 ? endpoint.scopes.join(", ") : "-"}
				</td>
				<td className="px-4 py-3 align-top">
					<span className={`${BADGE} ${scopeBadgeClass(endpoint.scope)}`}>
						{endpoint.scope === "tenant" ? "Override" : "Global"}
					</span>
				</td>
				<td className="px-4 py-3 align-top text-right">
					<div className="inline-flex gap-2">
						<button
							type="button"
							onClick={onToggle}
							className={BTN_SECONDARY}
						>
							{expanded ? "Hide" : "Schema"}
						</button>
						{endpoint.scope === "tenant" ? (
							<button
								type="button"
								onClick={onDelete}
								disabled={deleting}
								className={BTN_DANGER}
							>
								Remove
							</button>
						) : null}
					</div>
				</td>
			</tr>
			{expanded ? (
				<tr>
					<td colSpan={6} className="bg-zinc-50 px-4 py-4">
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<p className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">
									Request schema
								</p>
								<pre className="overflow-auto rounded-[6px] border border-zinc-200 bg-white p-3 text-xs text-zinc-800">
									<code>{endpoint.request_schema || "(empty)"}</code>
								</pre>
							</div>
							<div>
								<p className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">
									Response schema
								</p>
								<pre className="overflow-auto rounded-[6px] border border-zinc-200 bg-white p-3 text-xs text-zinc-800">
									<code>{endpoint.response_schema || "(empty)"}</code>
								</pre>
							</div>
						</div>
						<p className="mt-2 text-[11px] text-zinc-400">
							Updated {formatTimestamp(endpoint.updated_at)}
						</p>
					</td>
				</tr>
			) : null}
		</>
	);
}

export default SdkDocsPage;
